/**
 * dockLayout.ts — M115. ONE source of truth for the "glass panel docked down
 * the RIGHT, car in the clear zone on the LEFT" composition that the SHOP and
 * the GARAGE's CUSTOMIZE overlay now share (owner: Customize "should operate
 * similar to the existing module format and sizing on the Shop").
 *
 * The panel's width is declared in ui.css as `min(560px, 46vw)`; it is mirrored
 * here because the 3D car is placed in SCREEN fractions and therefore has to
 * know where the panel's left edge falls. Change one, change the other — the
 * two constants below are the only place the number lives on this side.
 */

/** ui.css `.shop-panel` / `.garage-cust`: width = min(PANEL_MAX_PX, PANEL_VW*w). */
export const PANEL_MAX_PX = 560;
export const PANEL_VW = 0.46;

/** Nudge right of the pure midpoint: the anchor sits under the car's mid-line,
 *  but a body rotating through three-quarter views reads slightly left-heavy
 *  against it (measured at 1280x800). */
const FAUX_CENTRE_PULL = 0.012;

/**
 * The horizontal viewport fraction that makes the car look CENTRED in the
 * clear zone left of the docked panel — i.e. an even gap from the screen's
 * left edge to the car and from the car to the panel's left edge (owner note
 * on the desktop shop). Derived from the LIVE viewport width, so the spacing
 * holds at every desktop width instead of only the one it was eyeballed at.
 */
export function fauxCentreX(viewportW: number): number {
  const panel = Math.min(PANEL_MAX_PX, PANEL_VW * viewportW);
  return (1 - panel / viewportW) / 2 + FAUX_CENTRE_PULL;
}

/* ---- M132 PORTRAIT (owner: "you currently can't see the vehicles when in
 * Customize and Shop mode" on a phone held upright). The panel is a BOTTOM
 * SHEET `PORTRAIT_SHEET_VH` of the viewport tall and the compact one-row
 * detail dock pins on top of it (ui.css `.shop-panel` / `.garage-cust` +
 * `.shop-detaildock`). The car used to be parked at a FIXED ground anchor
 * (y 0.47) that sat UNDER the dock's top edge, so its wheels hid behind the
 * dock and the zoom (2.5) ran the nose off the side of a 390px-wide screen.
 * Now the ground anchor is derived from the dock's LIVE top edge, so the car
 * always stands on the strip above the dock at every phone height, and the
 * zoom fits the whole body inside the width. Both screens share this so they
 * can never drift apart again. */

/** Mirrors ui.css: the portrait sheet height (46vh) + the dock's gap above it. */
export const PORTRAIT_SHEET_VH = 0.46;
export const PORTRAIT_DOCK_GAP_PX = 8;
/** Clear air between the car's wheels and the dock's top edge. */
const PORTRAIT_WHEEL_CLEAR_PX = 14;
/** Fov zoom that fits a side-on car inside a phone's width at the anchor
 *  depth below — measured live at 390x844: the pickup spans ~0.81 of the
 *  width, the monster truck (the longest, tallest ride) ~0.88. */
const PORTRAIT_ZOOM = 1.9;
/** The portrait TIRES & RIMS close-up: how much of the viewport height the
 *  tyre's diameter eats at most (M128c's 0.31), and the top strip it must
 *  clear (title + ride line, ~110px incl. the safe inset). */
const PORTRAIT_WHEEL_FILL = 0.31;
const PORTRAIT_TOP_STRIP_PX = 118;

/**
 * Where the car's GROUND contact lands in portrait: centred, just above the
 * detail dock. `dockTopPx` is the dock's measured top edge; pass null when it
 * is not laid out yet and the sheet geometry stands in for it.
 */
export function portraitCarAnchor(
  viewportH: number,
  dockTopPx: number | null,
): { x: number; y: number; zoom: number } {
  const dockTop =
    dockTopPx !== null && dockTopPx > 0
      ? dockTopPx
      : viewportH * (1 - PORTRAIT_SHEET_VH) - PORTRAIT_DOCK_GAP_PX - 66; // ~one-row dock
  const y = Math.min(0.48, Math.max(0.3, (dockTop - PORTRAIT_WHEEL_CLEAR_PX) / viewportH));
  return { x: 0.5, y, zoom: PORTRAIT_ZOOM };
}

/**
 * The portrait TIRES & RIMS close-up (shop): the wheel is centred in the band
 * between the top strip and the detail dock, and shrinks when that band is
 * too short for the full-size close-up (a Safari toolbar can leave a 660px
 * viewport) — its bottom used to sink under the dock there.
 */
export function portraitWheelAnchor(
  viewportH: number,
  dockTopPx: number | null,
): { xFrac: number; yFrac: number; fill: number } {
  const dockTop =
    dockTopPx !== null && dockTopPx > 0
      ? dockTopPx
      : viewportH * (1 - PORTRAIT_SHEET_VH) - PORTRAIT_DOCK_GAP_PX - 66;
  const band = Math.max(60, dockTop - PORTRAIT_TOP_STRIP_PX - 2 * PORTRAIT_WHEEL_CLEAR_PX);
  const fill = Math.min(PORTRAIT_WHEEL_FILL, band / viewportH);
  const centre = dockTop - PORTRAIT_WHEEL_CLEAR_PX - (fill * viewportH) / 2;
  return { xFrac: 0.5, yFrac: centre / viewportH, fill };
}
